import React from 'react';
import Spacer from 'common/components/Spacer';
import get from 'lodash/get';
import { useEditor } from '@splashthat/splash-cms-engine';
import { SidebarPanel, SPACING } from 'common/components/TouchpointEditor/components/TouchpointSidebar';
import ShowHideButton from 'common/components/TouchpointEditor/components/TouchpointSidebar/ShowHideButton';
import { containerPaddingL } from 'common/styles/variables';
import {
    NodeContainer,
    NodeButton,
} from 'common/components/TouchpointEditor/components/TouchpointSidebar/ElementsListMenu';

import {
    // NODE IDS
    NODE_ID_VIRTUAL_CONTENT,
    NODE_ID_EVENT_TITLE,
    NODE_ID_SESSION_INFORMATION,
    NODE_ID_SPEAKER_INFORMATION,
    NODE_ID_LEAVE_BUTTON,
    NODE_ID_UPCOMING_SESSIONS_BLOCK,
    NODE_ID_UPCOMING_SESSIONS_HEADLINE,
    NODE_ID_UPCOMING_SESSIONS_PARAGRAPH,
    NODE_ID_UPCOMING_SESSION_CARDS,
    NODE_ID_PAST_SESSIONS_BLOCK,
    NODE_ID_PAST_SESSIONS_HEADLINE,
    NODE_ID_PAST_SESSIONS_PARAGRAPH,
    NODE_ID_PAST_SESSION_CARDS,
    NODE_ID_SPEAKER_MODALS,
    NODE_ID_FOOTER,
    NODE_ID_CONTACT_ORGANIZER_BUTTON,
    NODE_ID_VISIT_EVENT_PAGE_BUTTON,
    NODE_ID_CUSTOM_BUTTON,
    NODE_ID_CUSTOM_BUTTON_TWO,

    // NODE DISPLAY NAMES
    NODE_DISPLAY_NAME_VIRTUAL_CONTENT,
    NODE_DISPLAY_NAME_EVENT_TITLE,
    NODE_DISPLAY_NAME_SESSION_INFORMATION,
    NODE_DISPLAY_NAME_SPEAKER_INFORMATION,
    NODE_DISPLAY_NAME_LEAVE_BUTTON,
    NODE_DISPLAY_NAME_UPCOMING_SESSIONS_BLOCK,
    NODE_DISPLAY_NAME_UPCOMING_SESSIONS_HEADLINE,
    NODE_DISPLAY_NAME_UPCOMING_SESSIONS_PARAGRAPH,
    NODE_DISPLAY_NAME_UPCOMING_SESSION_CARDS,
    NODE_DISPLAY_NAME_PAST_SESSIONS_BLOCK,
    NODE_DISPLAY_NAME_PAST_SESSIONS_HEADLINE,
    NODE_DISPLAY_NAME_PAST_SESSIONS_PARAGRAPH,
    NODE_DISPLAY_NAME_PAST_SESSION_CARDS,
    NODE_DISPLAY_NAME_SPEAKER_MODALS,
    NODE_DISPLAY_NAME_FOOTER,
    NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON,
    NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO,
} from '../../constants';

export default function ElementsListMenu() {
    const editor = useEditor((state) => ({
        nodes: {
            [NODE_ID_UPCOMING_SESSIONS_HEADLINE]: state.nodes[NODE_ID_UPCOMING_SESSIONS_HEADLINE],
            [NODE_ID_UPCOMING_SESSIONS_PARAGRAPH]: state.nodes[NODE_ID_UPCOMING_SESSIONS_PARAGRAPH],
            [NODE_ID_PAST_SESSIONS_HEADLINE]: state.nodes[NODE_ID_PAST_SESSIONS_HEADLINE],
            [NODE_ID_PAST_SESSIONS_PARAGRAPH]: state.nodes[NODE_ID_PAST_SESSIONS_PARAGRAPH],
            [NODE_ID_CUSTOM_BUTTON]: state.nodes[NODE_ID_CUSTOM_BUTTON],
            [NODE_ID_CUSTOM_BUTTON_TWO]: state.nodes[NODE_ID_CUSTOM_BUTTON_TWO],
        },
    }));

    const selectNode = (e, id) => {
        e.stopPropagation();
        editor.actions.selectNode(id);
    };

    const toggleNodeVisibility = (e, id) => {
        e.stopPropagation();
        editor.actions.setNodeProps(id, (node) => {
            // eslint-disable-next-line no-param-reassign
            node.meta.visible = !node.meta.visible;

            return node;
        });
    };

    return (
        <SidebarPanel variation="padded">
            <NodeContainer>
                <NodeButton
                    paddingLeft="0"
                    variation="navigation"
                    label={NODE_DISPLAY_NAME_VIRTUAL_CONTENT}
                    qaId={NODE_ID_VIRTUAL_CONTENT}
                    onClick={(e) => selectNode(e, NODE_ID_VIRTUAL_CONTENT)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_VIRTUAL_CONTENT)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                    bold
                />

                <NodeButton
                    label={NODE_DISPLAY_NAME_EVENT_TITLE}
                    variation="secondary"
                    qaId={NODE_ID_EVENT_TITLE}
                    onClick={(e) => selectNode(e, NODE_ID_EVENT_TITLE)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_EVENT_TITLE)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_SESSION_INFORMATION}
                    variation="secondary"
                    qaId={NODE_ID_SESSION_INFORMATION}
                    onClick={(e) => selectNode(e, NODE_ID_SESSION_INFORMATION)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_SESSION_INFORMATION)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_SPEAKER_INFORMATION}
                    variation="secondary"
                    qaId={NODE_ID_SPEAKER_INFORMATION}
                    onClick={(e) => selectNode(e, NODE_ID_SPEAKER_INFORMATION)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_SPEAKER_INFORMATION)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_LEAVE_BUTTON}
                    variation="secondary"
                    qaId={NODE_ID_LEAVE_BUTTON}
                    onClick={(e) => selectNode(e, NODE_ID_LEAVE_BUTTON)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_LEAVE_BUTTON)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
            </NodeContainer>
            <Spacer $spacing={containerPaddingL} />

            <NodeContainer>
                <NodeButton
                    paddingLeft="0"
                    variation="navigation"
                    label={NODE_DISPLAY_NAME_UPCOMING_SESSIONS_BLOCK}
                    qaId={NODE_ID_UPCOMING_SESSIONS_BLOCK}
                    onClick={(e) => selectNode(e, NODE_ID_UPCOMING_SESSIONS_BLOCK)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_UPCOMING_SESSIONS_BLOCK)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                    bold
                />

                <NodeButton
                    label={NODE_DISPLAY_NAME_UPCOMING_SESSIONS_HEADLINE}
                    variation="secondary"
                    iconAfter={
                        <ShowHideButton
                            handleToggle={(e) => toggleNodeVisibility(e, NODE_ID_UPCOMING_SESSIONS_HEADLINE)}
                            visible={get(editor.nodes, [NODE_ID_UPCOMING_SESSIONS_HEADLINE, 'meta', 'visible'])}
                        />
                    }
                    qaId={NODE_ID_UPCOMING_SESSIONS_HEADLINE}
                    onClick={(e) => selectNode(e, NODE_ID_UPCOMING_SESSIONS_HEADLINE)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_UPCOMING_SESSIONS_HEADLINE)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_UPCOMING_SESSIONS_PARAGRAPH}
                    variation="secondary"
                    iconAfter={
                        <ShowHideButton
                            handleToggle={(e) => toggleNodeVisibility(e, NODE_ID_UPCOMING_SESSIONS_PARAGRAPH)}
                            visible={get(editor.nodes, [NODE_ID_UPCOMING_SESSIONS_PARAGRAPH, 'meta', 'visible'])}
                        />
                    }
                    qaId={NODE_ID_UPCOMING_SESSIONS_PARAGRAPH}
                    onClick={(e) => selectNode(e, NODE_ID_UPCOMING_SESSIONS_PARAGRAPH)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_UPCOMING_SESSIONS_PARAGRAPH)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_UPCOMING_SESSION_CARDS}
                    variation="secondary"
                    qaId={NODE_ID_UPCOMING_SESSION_CARDS}
                    onClick={(e) => selectNode(e, NODE_ID_UPCOMING_SESSION_CARDS)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_UPCOMING_SESSION_CARDS)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
            </NodeContainer>
            <Spacer $spacing={containerPaddingL} />

            <NodeContainer>
                <NodeButton
                    paddingLeft="0"
                    variation="navigation"
                    label={NODE_DISPLAY_NAME_PAST_SESSIONS_BLOCK}
                    qaId={NODE_ID_PAST_SESSIONS_BLOCK}
                    onClick={(e) => selectNode(e, NODE_ID_PAST_SESSIONS_BLOCK)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_PAST_SESSIONS_BLOCK)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                    bold
                />

                <NodeButton
                    label={NODE_DISPLAY_NAME_PAST_SESSIONS_HEADLINE}
                    variation="secondary"
                    iconAfter={
                        <ShowHideButton
                            handleToggle={(e) => toggleNodeVisibility(e, NODE_ID_PAST_SESSIONS_HEADLINE)}
                            visible={get(editor.nodes, [NODE_ID_PAST_SESSIONS_HEADLINE, 'meta', 'visible'])}
                        />
                    }
                    qaId={NODE_ID_PAST_SESSIONS_HEADLINE}
                    onClick={(e) => selectNode(e, NODE_ID_PAST_SESSIONS_HEADLINE)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_PAST_SESSIONS_HEADLINE)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_PAST_SESSIONS_PARAGRAPH}
                    variation="secondary"
                    iconAfter={
                        <ShowHideButton
                            handleToggle={(e) => toggleNodeVisibility(e, NODE_ID_PAST_SESSIONS_PARAGRAPH)}
                            visible={get(editor.nodes, [NODE_ID_PAST_SESSIONS_PARAGRAPH, 'meta', 'visible'])}
                        />
                    }
                    qaId={NODE_ID_PAST_SESSIONS_PARAGRAPH}
                    onClick={(e) => selectNode(e, NODE_ID_PAST_SESSIONS_PARAGRAPH)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_PAST_SESSIONS_PARAGRAPH)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_PAST_SESSION_CARDS}
                    variation="secondary"
                    qaId={NODE_ID_PAST_SESSION_CARDS}
                    onClick={(e) => selectNode(e, NODE_ID_PAST_SESSION_CARDS)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_PAST_SESSION_CARDS)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
            </NodeContainer>
            <Spacer $spacing={containerPaddingL} />

            <NodeButton
                label={NODE_DISPLAY_NAME_SPEAKER_MODALS}
                variation="secondary"
                qaId={NODE_ID_SPEAKER_MODALS}
                onClick={(e) => selectNode(e, NODE_ID_SPEAKER_MODALS)}
                onMouseEnter={() => editor.actions.hoverNode(NODE_ID_SPEAKER_MODALS)}
                onMouseLeave={() => editor.actions.unhoverAllNodes()}
                bold
            />

            <Spacer $spacing={containerPaddingL} />
            <NodeContainer>
                <NodeButton
                    paddingLeft="0"
                    variation="navigation"
                    label={NODE_DISPLAY_NAME_FOOTER}
                    qaId={NODE_ID_FOOTER}
                    onClick={(e) => selectNode(e, NODE_ID_FOOTER)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_FOOTER)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                    bold
                />

                <NodeButton
                    label={NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON}
                    variation="secondary"
                    qaId={NODE_ID_CONTACT_ORGANIZER_BUTTON}
                    onClick={(e) => selectNode(e, NODE_ID_CONTACT_ORGANIZER_BUTTON)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_CONTACT_ORGANIZER_BUTTON)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON}
                    variation="secondary"
                    qaId={NODE_ID_VISIT_EVENT_PAGE_BUTTON}
                    onClick={(e) => selectNode(e, NODE_ID_VISIT_EVENT_PAGE_BUTTON)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_VISIT_EVENT_PAGE_BUTTON)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_CUSTOM_BUTTON}
                    variation="secondary"
                    iconAfter={
                        <ShowHideButton
                            handleToggle={(e) => toggleNodeVisibility(e, NODE_ID_CUSTOM_BUTTON)}
                            visible={get(editor.nodes, [NODE_ID_CUSTOM_BUTTON, 'meta', 'visible'])}
                        />
                    }
                    qaId={NODE_ID_CUSTOM_BUTTON}
                    onClick={(e) => selectNode(e, NODE_ID_CUSTOM_BUTTON)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_CUSTOM_BUTTON)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO}
                    variation="secondary"
                    iconAfter={
                        <ShowHideButton
                            handleToggle={(e) => toggleNodeVisibility(e, NODE_ID_CUSTOM_BUTTON_TWO)}
                            visible={get(editor.nodes, [NODE_ID_CUSTOM_BUTTON_TWO, 'meta', 'visible'])}
                        />
                    }
                    qaId={NODE_ID_CUSTOM_BUTTON_TWO}
                    onClick={(e) => selectNode(e, NODE_ID_CUSTOM_BUTTON_TWO)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_CUSTOM_BUTTON_TWO)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
            </NodeContainer>
        </SidebarPanel>
    );
}
